import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { FaCamera, FaVideo, FaTrash, FaHome, FaChevronLeft, FaSpinner } from 'react-icons/fa';
import { StandardURL } from "../App";


// Custom Modal Component for better styling consistency
const CustomModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 transition-opacity duration-300 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
                <div className="flex justify-between items-center border-b p-4">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>
                <div className="p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

const SpecificRental = () => {
    const [rental, setRental] = useState({});
    const [photos, setPhotos] = useState([]);
    const [videos, setVideos] = useState([]);
    const [showAddPhotoModal, setShowAddPhotoModal] = useState(false);
    const [showAddVideoModal, setShowAddVideoModal] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [token, setToken] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const navigate = useNavigate();

    // Re-fetch helper to update media lists after successful add/delete
    const fetchMedia = (rentalId, token) => {
        // Fetch photos
        axios.get(`${StandardURL}/iNyumba/photos/fetch/${rentalId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(response => setPhotos(response.data.response || []))
            .catch(error => console.error("Error fetching photos:", error));

        // Fetch videos
        axios.get(`${StandardURL}/iNyumba/videos/fetch/${rentalId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(response => setVideos(response.data.response || []))
            .catch(error => console.error("Error fetching videos:", error));
    };


    useEffect(() => {
        const rentalId = localStorage.getItem("selectedRentalId");
        const token = localStorage.getItem("token");

        if (!rentalId || !token) {
            toast.error("Rental ID or token missing.");
            navigate('/landlord/rentals'); // Redirect if essential data is missing
            return;
        }

        setToken(token);

        // Fetch rental data
        axios.get(`${StandardURL}/iNyumba/admin/rentals/specific/${rentalId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then(response => {
            setRental(response.data.response || {});
        })
        .catch(error => {
            console.error("Error fetching rental:", error);
            toast.error("Failed to load rental details.");
        })
        .finally(() => {
             // Fetch photos and videos
             fetchMedia(rentalId, token);
             setIsLoading(false);
        });
    }, [navigate]);

    const handleAddPhoto = () => {
        setPhotoFile(null);
        setShowAddPhotoModal(true);
    };

    const handleAddVideo = () => {
        setVideoFile(null);
        setShowAddVideoModal(true);
    };

    const closeModal = () => {
        setShowAddPhotoModal(false);
        setShowAddVideoModal(false);
    };

    const handlePhotoFileChange = (e) => {
        setPhotoFile(e.target.files[0]);
    };

    const handleVideoFileChange = (e) => {
        setVideoFile(e.target.files[0]);
    };

    const addPhoto = async () => {
        if (!photoFile) {
            toast.warn("Please select a photo file.");
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append("image", photoFile);

        try {
            const uploadResponse = await axios.post(
                `${StandardURL}/iNyumba/images/images/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const newPhoto = { photo_url: uploadResponse.data.response.coverUri };
            
            // Step 2: Add photo to rental
            await addPhotoToRental(newPhoto);

            // Manual update and close
            setPhotos(prev => [...prev, { ...newPhoto, photo_id: Date.now() }]); // Add a temporary ID for keying
            closeModal();

        } catch (error) {
            const errorMessage = error.response?.data?.response || "Error uploading photo.";
            toast.error(errorMessage);
            console.error("Error in photo process:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const addPhotoToRental = (photo) => {
        const rentalId = localStorage.getItem("selectedRentalId");
        return axios.post(
            `${StandardURL}/iNyumba/photos/add/${rentalId}`,
            { photo_url: photo.photo_url },
            { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((response) => {
            toast.success(response.data.response);
            // Re-fetch media to get the final photo_id from the backend
            fetchMedia(rentalId, token);
        })
        .catch((error) => {
            const errorMessage = error.response?.data?.response || "Error adding photo to rental.";
            toast.error(errorMessage);
            throw new Error(errorMessage); // Re-throw to be caught in the main function
        });
    };

    const handleDeletePhoto = (photoId) => {
        localStorage.setItem("deletePhotoId", photoId);
        navigate("/landlord/deletephoto");
    };

    const handleDeleteVideo = (videoId) => {
        localStorage.setItem("deleteVideoId", videoId);
        navigate("/landlord/deletevideo");
    };

    const addVideo = async () => {
        if (!videoFile) {
            toast.warn("Please select a video file.");
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append("video", videoFile);

        try {
            const uploadResponse = await axios.post(
                `${StandardURL}/iNyumba/images/images/upload`, // API URL for both image and video uploads
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const newVideo = { video_url: uploadResponse.data.response.coverUri };
            
            // Step 2: Add video to rental
            await addVideoToRental(newVideo);

            // Manual update and close
            setVideos(prev => [...prev, { ...newVideo, video_id: Date.now() }]); // Add a temporary ID for keying
            closeModal();

        } catch (error) {
            const errorMessage = error.response?.data?.response || "Error uploading video.";
            toast.error(errorMessage);
            console.error("Error in video process:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const addVideoToRental = (video) => {
        const rentalId = localStorage.getItem("selectedRentalId");
        return axios.post(
            `${StandardURL}/iNyumba/videos/add/${rentalId}`,
            { video_url: video.video_url },
            { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((response) => {
            toast.success(response.data.response);
            // Re-fetch media to get the final video_id from the backend
            fetchMedia(rentalId, token);
        })
        .catch((error) => {
            const errorMessage = error.response?.data?.response || "Error adding video to rental.";
            toast.error(errorMessage);
            throw new Error(errorMessage); // Re-throw to be caught in the main function
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <FaSpinner className="animate-spin text-4xl text-blue-600" />
                <p className="ml-3 text-lg text-gray-600">Loading rental details...</p>
            </div>
        );
    }
    
    const goBack = () => navigate(-1);

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">                    
                    <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-xl p-6 lg:p-8">
                        
                        {/* Header and Back Button */}
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <button
                                onClick={goBack}
                                className="text-gray-600 hover:text-blue-600 transition duration-150 flex items-center"
                            >
                                <FaChevronLeft className="mr-2" /> Back to Rentals
                            </button>
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaHome className="mr-3 text-blue-600" /> Rental Unit: {rental.house_number}
                            </h1>
                        </div>

                        {/* Rental Details Card */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-8 shadow-sm">
                            <h2 className="text-xl font-bold text-blue-800 mb-3">Unit Information</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
                                <p>
                                    <strong>Unit Number:</strong> <span className="font-semibold">{rental.house_number}</span>
                                </p>
                                <p className="col-span-full">
                                    <strong>Description:</strong> {rental.description || 'N/A'}
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 mb-10">
                            <button
                                onClick={handleAddPhoto}
                                className="flex items-center bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200"
                            >
                                <FaCamera className="mr-2" /> Add Photo
                            </button>

                            <button
                                onClick={handleAddVideo}
                                className="flex items-center bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200"
                            >
                                <FaVideo className="mr-2" /> Add Video
                            </button>
                        </div>

                        {/* Photos Section */}
                        <div className="mt-8">
                            <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mb-4">Photos ({photos.length})</h2>
                            {photos.length === 0 ? (
                                <p className="text-gray-500">No photos added for this rental unit.</p>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {photos.map((photo, index) => (
                                        <div key={photo.photo_id || index} className="relative group overflow-hidden rounded-lg shadow-lg">
                                            <img
                                                src={photo.photo_url}
                                                alt={`rental photo ${index}`}
                                                className="w-full h-48 object-cover transition duration-300 group-hover:scale-105"
                                            />
                                            {/* Delete Overlay Button */}
                                            <button
                                                onClick={() => handleDeletePhoto(photo.photo_id)}
                                                className="absolute inset-0 flex items-center justify-center bg-red-600 bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white font-bold p-2"
                                                title="Delete Photo"
                                            >
                                                <FaTrash className="mr-2" /> Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Videos Section */}
                        <div className="mt-12">
                            <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mb-4">Videos ({videos.length})</h2>
                            {videos.length === 0 ? (
                                <p className="text-gray-500">No videos added for this rental unit.</p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {videos.map((video, index) => (
                                        <div key={video.video_id || index} className="relative group rounded-lg shadow-xl overflow-hidden">
                                            <div className="aspect-w-16 aspect-h-9">
                                                <iframe
                                                    title={`rental video ${index}`}
                                                    src={video.video_url}
                                                    className="w-full h-full"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                ></iframe>
                                            </div>
                                            {/* Delete Overlay Button */}
                                            <button
                                                onClick={() => handleDeleteVideo(video.video_id)}
                                                className="absolute bottom-0 w-full flex items-center justify-center bg-red-600 bg-opacity-90 text-white font-bold py-2 transition duration-300 opacity-0 group-hover:opacity-100"
                                                title="Delete Video"
                                            >
                                                <FaTrash className="mr-2" /> Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
                
                {/* Modal for adding photos */}
                <CustomModal
                    isOpen={showAddPhotoModal}
                    onClose={closeModal}
                    title="Upload New Photo"
                >
                    <div className="mb-4">
                        <label
                            htmlFor="photoFile"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Select Photo File:
                        </label>
                        <input
                            type="file"
                            id="photoFile"
                            className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                            onChange={handlePhotoFileChange}
                            accept="image/*"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={addPhoto}
                            disabled={isUploading || !photoFile}
                            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 disabled:bg-blue-400"
                        >
                            {isUploading ? <><FaSpinner className="animate-spin mr-2" /> Uploading...</> : 'Add Photo'}
                        </button>
                        <button
                            onClick={closeModal}
                            type="button"
                            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </CustomModal>

                {/* Modal for adding videos */}
                <CustomModal
                    isOpen={showAddVideoModal}
                    onClose={closeModal}
                    title="Upload New Video"
                >
                    <div className="mb-4">
                        <label
                            htmlFor="videoFile"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Select Video File:
                        </label>
                        <input
                            type="file"
                            id="videoFile"
                            className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                            onChange={handleVideoFileChange}
                            accept="video/*"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={addVideo}
                            disabled={isUploading || !videoFile}
                            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 disabled:bg-blue-400"
                        >
                            {isUploading ? <><FaSpinner className="animate-spin mr-2" /> Uploading...</> : 'Add Video'}
                        </button>
                        <button
                            onClick={closeModal}
                            type="button"
                            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </CustomModal>
            </AuthGuard>
        </EmailGuard>
    );
};

export default SpecificRental;